import { io, Socket } from 'socket.io-client';
import FormData from 'form-data';
import moment from 'moment';
import { initialTwilioChatState } from '../utils/chats';
import { createLeadContactBillingAddressPayload } from '../utils/helperFn';
import API from '../api';
export interface Message {
  createdAt: string;
  from?: string | null;
  files?: string | null;
  message: string | null;
  form: FormType | null;
  messageId: string;
  fromId: string;
  senderType: string;
}
export interface FormType {
  type: string;
  value: Array<{ title: string; action: string; placeholder?: string | null; type?: string; required?: boolean; options?:object }>;
}

export const fetchAsync = async (url = '', method = 'get', headers:any = {}, body:any = {}) => {
  try {
    let conn:any;

    switch (method.toLowerCase()) {
      case 'get': conn = await API.get(url, headers); break;
      case 'post': conn = await API.post(url, body, headers); break;
      case 'put': conn = await API.put(url, body, headers); break;
      case 'patch': conn = await API.patch(url, body, headers); break;
      case 'delete': conn = await API.delete(url, headers); break;  
      default: conn = await API.get(url, headers); break;
    }
    
    if(conn){
      return conn;
    } else {
      return null;
    }
  } catch (error:any) {
    console.error('API is crashing:', error);
    throw error;
  }
};

export const loadZipCodesAsync = async (input: string): Promise<{ value: string; label: string; SPRN: string; UDPRN: string; UPRN: string; UMPRN: string; }[]> => {
    try {
      const data = await fetchAsync(`/CrmModule/v2.0/premises/post-code-checker?searchString=${input}`, 'get', null, null);
      return data.data.map((category: any) => ({
        value: category.properties.PostalCode,
        label: category.properties.FullAddress,
        SPRN: category.properties.SPRN,
        UDPRN: category.properties.UDPRN,
        UPRN: category.properties.UPRN,
        UMPRN: category.properties.UMPRN,
      }));
    } catch (error) {
      console.error('Error fetching ZIP codes:', error);
      return [];
    }
};

export const validatePhone = async (phoneNumber: string): Promise<boolean> => {
  if(phoneNumber.length === 0) return true;
  try {
    const data = await fetchAsync(`/CrmModule/twilio/v1.0/+44${phoneNumber}/lookup`, 'get', null, null);
    if (data) return data?.data?.valid;
    return false;
  } catch (error) {
    console.error('Error validating phone number:', error);
    return false;
  }
};

export const getToken = async (email) => {
  try {
    const data = await fetchAsync(`/ChatModule/v1.0/gateway/token?identity=${email}&source=twilio`, 'get', null, null);
    return data;
  } catch (error) { console.error('Error fetching token:', error); }
};

export const chatLocalStorage = (name:string|null = null) => {
  const response =  JSON.parse(localStorage.getItem('twilioChat') || JSON.stringify(initialTwilioChatState));
  return name ? response[name] : response
};

export const didAgentPost = (messages) => {
  return messages && chatLocalStorage('chatConversationId') ? Object.keys(messages.filter(ext => ext.senderType === 'AGENT')).length  > 1 : false;
};

export const scrollToBottom = () => {
  try {
    const scrollableContainer = document.querySelector('#ChatContentList');
    if(scrollableContainer){
      scrollableContainer.scrollTo({
        top: scrollableContainer.scrollHeight + 100,
        behavior: 'smooth'
      });
    }
  } catch(e){ }
};

export const isConversationActive = async () => {
  if(chatLocalStorage('chatConversationId')){ 
    try {
      const response = await fetchAsync(`/ChatModule/v1.0/gateway/conversation/${chatLocalStorage('chatConversationId')}`, 'get', null, null);
      // Check if the response is okay
      if (!response) {
        console.error("Can't check conversation!");
        return null;
      }
      return response?.data?.properties?.Status;
    } catch (error) { console.error('Error fetching conversation status: ', error); }
  }
};

export const createConversation = async (data:any) => { 
  const{firstName, lastName, email, phone, address, postalCode, SPRN} = data;   
  try {
    const response = await fetchAsync(`/ChatModule/v1.0/gateway/conversation/create`, 'post', null, 
      {
        source: "twilio",
        channel: "WEB_CHAT",
        identifier: email || null,
        DpaCustomerName: `${firstName} ${lastName}` || null,
        DpaCustomerEmail: email || null,
        DpaCustomerAddress: address || null,
        DpaCustomerPhone: phone || null,
      }
    );
    // Check if the response is okay
    if (!response) {
      console.error("Can't create conversation!");
      return null;
    } else {
      console.log('Conversation created');
      return response;
    }
  } catch (error:any) { 
    return Object.hasOwn(error, 'response')? error?.response?.data : error;
  }
};

export const closeConversation = async (closedBy:string = "CUSTOMER") => {
  try {
    const response = await fetchAsync(
      `/ChatModule/v1.0/gateway/conversation/${chatLocalStorage('chatConversationId')}/close`, 
      'post', 
      null, 
      { closedBy: closedBy }
    );
    // Check if the response is okay
    if (!response) {
      console.error("Can't close conversation!");
      return null;
    }
    // Check if the statusCode matches
    return response?.statusCode === 201;
  } catch (error) { console.error('Error closing conversation:', error); }
};

export const sendMessage = async (message:string|null = null, uploadFiles:string[] = [], senderType:string = 'CUSTOMER') => {
  if(message || (message && message.length > 0) || Object.keys(uploadFiles).length > 0) {
    typeof document !== "undefined" && document.getElementById('inputMessage')?.focus();
    let formData;
    if(Object.keys(uploadFiles).length > 0){
      let tmpFormData:FormData = new FormData();
        tmpFormData.append('conversationSid', chatLocalStorage('chatConversationId'));
        tmpFormData.append('message', message !== '' ? message : null);
        tmpFormData.append('from', chatLocalStorage('firstName'));
        tmpFormData.append('sender', senderType);
        try{
          Object.keys(uploadFiles).length > 0 && uploadFiles.map(async (item) => {
            tmpFormData.append('files', item);
          });  
        } catch (e){
  
        }
        formData = tmpFormData;
    } else {
      let tmpFormData:any = {
        conversationSid: chatLocalStorage('chatConversationId'),
        message: message,
        from: chatLocalStorage('firstName'),
        sender: 'CUSTOMER',
        files: null,
      };
      formData = tmpFormData;
    }
    // Send a Message to the Conversation
    try {      
      const data = await fetchAsync(`/ChatModule/v1.0/gateway/conversation/${chatLocalStorage('chatConversationId')}/message/send`, 'post', null, formData);
      return data;
    } catch (error) {
      console.error('Send a Message:', error);
      return [];
    }
  }
};
  
export const getAllMessages = async () => {
  try{
    if(chatLocalStorage('chatConversationId')){
      const response:any = await fetchAsync(`/NotificationModule/v2.0/records/Conversation/${chatLocalStorage('chatConversationId')}/links?targetEntities=["Message"]`, 'get', null, null);
      if(response){
        let tmpMessages:Message[] = await Promise.all(
          response.data?.Message?.dbRecords.map(async item => ({
            createdAt: item?.createdAt,
            from: item?.properties?.From,
            files: await getFiles(item?.properties?.Files), 
            message: item?.properties?.Body,
            form: null,
            messageId: item?.id,
            fromId: item?.createdBy?.id,
            senderType: item.type === "EVENT" ? "EVENT" : item.properties.SenderType
          }))
        );         
        return tmpMessages ? [
          ...tmpMessages, {
          senderType: 'EVENT',
          createdAt: moment(new Date()).format(),
          from: 'YouFibre',
          message: "Waiting for the agent to join the chat.",
          form: null,
          files: [],
          messageId: `${Date.now()}-0000-0000-0000-000000000000`,
          fromId: 'YouFibre',
        }] : [];   
      }
    }
  } catch (error) {
    console.error('Error fetching messages:', error);
    return [];
  }
};

export const findUnreadedMessages = async (oldMessages:Message[] = [], newMessages:Message[] = []) => {
    let tmpMessages:Message[] = await Promise.all(
        newMessages.filter(item => !oldMessages.some(obj => obj?.messageId === item?.messageId)));       
    return tmpMessages;
};  

export const connectToWebsocket = (sessionId:string|null = null) => {
  return io(`${process.env.GATSBY_TWILIO_WEBSOCKET}/ChatWebsocketModule/TwilioChatWebsocket/v1.0`, 
    { 
      path: '/ws',
      transports: ['websocket'],
      query: {
        clientId: sessionId 
      },
      reconnection: true,
      reconnectionAttempts: Infinity, // Retry indefinitely
      reconnectionDelay: 1000,       // Start with a 1-second delay
      reconnectionDelayMax: 5000,    // Cap the delay at 5 seconds
      timeout: 10000,                // Timeout after 10 seconds
    }); 
};

export const getFiles = async (files: String[] | null = null) => {
  try{
    let tmpFiles:any = [];
    if(files){
      const response:any = await fetchAsync(`/SchemaModule/v1.0/db/File/many?ids=${files}`, 'get', null, null);
        // Check if the response is okay
      if (!response) {
        console.error("Can't close conversation!");
      } else {
        if(Object.keys(response?.data).length){
          response?.data?.map((item) => {
            tmpFiles = [...tmpFiles, {
              id: item?.id || "",
              type: item?.properties?.Mimetype || "",
              url: item?.properties?.Url || ""
            }];
          });
        }
        return (Object.keys(tmpFiles).length !== 0) ? tmpFiles : [];
      }
    } else {
      return [];
    }
  } catch (error) {
    console.error('Error fetching files:', error);
    return [];
  }
};

export const createLead = async (FormData) => {
  try { 
    const response = await fetchAsync(`/CrmModule/v1.0/db/batch`, 'post', null, createLeadContactBillingAddressPayload(FormData));
    // Check if the response is okay
    if (!response) {
      console.error("Can't create Lead!");
      return null;
    }
    return response;
  } catch(e){ }
};

export const createCase = async (payload:any = {}) => {
  try { 
    const response = await fetchAsync(`/CrmModule/v1.0/db/batch`, 'post', null, payload);
    if (!response) {
      console.error("Can't create Case!");
      return null;
    }
    return response;
  } catch(e){ }
};

export const createEmailCase = async (payload:any = {}) => {
  try {
    const response:any = await fetchAsync(`/ChatModule/v1.0/gateway/conversation/create-email-case`, 'POST', null, payload);
    if (!response) {
      console.error("Can't update Case!");
      return false;
    } else {
      return true;
    }
  } catch (e) {
    console.log('error: ', e);
    return false;
  }
};
